import { useId, useState, type MouseEvent as ReactMouseEvent } from "react";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";

import {
  KPI_GOAL_LINE_PERCENT,
  SCREEN_HOURS_GREEN_MAX,
  STAFFER_STAT_GOAL_LINE_PERCENT,
} from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { dashboardStafferStats } from "@/lib/dashboardStafferStats";
import { TrendWeekTooltip } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/TrendWeekTooltip";

import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { GoalPeriod, PerformanceTier, StafferStatKind } from "@/types";

// Line and dot colors, matching the performance gradient bars in index.css.
const TIER_COLOR: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]: "rgb(var(--lever-icon-success))",
  [PerformanceTier.ORANGE]: "rgb(var(--lever-icon-warning))",
  [PerformanceTier.RED]: "rgb(var(--lever-icon-error))",
};

// The trend always plots whole Saturday-Friday weeks.
const TREND_PERIOD = GoalPeriod.WEEK;

// Catmull-Rom converted to cubic beziers; 6 is the standard tension divisor,
// which puts each control point a sixth of the way along its neighbour's span.
const BEZIER_TENSION_DIVISOR = 6;

// Half the gap between two points, in viewBox units, over which a color change
// blends rather than switching hard at the midpoint.
const COLOR_BLEND_HALF_WIDTH = 4;

// The smoothing overshoots past the plotted points, so the area's mask has to
// reach above the highest one or the fill gets erased where the curve bulges.
const MASK_OVERSHOOT_PERCENT = 40;

interface TrendPoint {
  label: string;
  weekStart: string;
  tier: PerformanceTier | null;
  value: number | null;
  x: number;
  y: number | null;
}

const smoothPath = (points: { x: number; y: number }[]): string => {
  if (points.length === 0) return "";
  if (points.length === 1) {
    return `M ${points[0].x.toString()} ${points[0].y.toString()}`;
  }

  return points.slice(0, -1).reduce((path, point, index) => {
    const previous = points[index - 1] ?? point;
    const next = points[index + 1];
    const afterNext = points[index + 2] ?? next;
    const firstControlX =
      point.x + (next.x - previous.x) / BEZIER_TENSION_DIVISOR;
    const firstControlY =
      point.y + (next.y - previous.y) / BEZIER_TENSION_DIVISOR;
    const secondControlX =
      next.x - (afterNext.x - point.x) / BEZIER_TENSION_DIVISOR;
    const secondControlY =
      next.y - (afterNext.y - point.y) / BEZIER_TENSION_DIVISOR;

    return `${path} C ${firstControlX.toString()} ${firstControlY.toString()}, ${secondControlX.toString()} ${secondControlY.toString()}, ${next.x.toString()} ${next.y.toString()}`;
  }, `M ${points[0].x.toString()} ${points[0].y.toString()}`);
};

// One stop per plotted point, with a short blend either side of each color
// change so the line shifts tier gradually instead of switching mid-segment.
const gradientStops = (
  plotted: { x: number; tier: PerformanceTier }[],
): { color: string; offset: number }[] => {
  const stops = [{ color: TIER_COLOR[plotted[0].tier], offset: 0 }];

  plotted.slice(0, -1).forEach((point, index) => {
    const next = plotted[index + 1];
    if (point.tier === next.tier) return;

    const boundary = (point.x + next.x) / 2;
    stops.push(
      {
        color: TIER_COLOR[point.tier],
        offset: Math.max(0, boundary - COLOR_BLEND_HALF_WIDTH),
      },
      {
        color: TIER_COLOR[next.tier],
        offset: Math.min(100, boundary + COLOR_BLEND_HALF_WIDTH),
      },
    );
  });

  stops.push({
    color: TIER_COLOR[plotted[plotted.length - 1].tier],
    offset: 100,
  });
  return stops;
};

// Five weeks of a single stat, as a tier-colored line over a goal line. A week
// with no data breaks the labels, not the line. `kind` supplies the default
// grading and formatting; the overrides let a caller plot any weekly series.
export const DashboardStafferTrendChart = <TRow extends { weekStart: string }>({
  chartTopPercent = 0,
  emptyLabel,
  formatValue,
  goalOverride,
  isLowerBetter,
  kind,
  rows,
  tierForValue,
  valueForRow,
  onOpenScreenBreakdown,
}: {
  emptyLabel?: string;
  formatValue?: (value: number | null) => string;
  chartTopPercent?: number;
  goalOverride?: number;
  isLowerBetter?: boolean;
  kind: StafferStatKind;
  rows: TRow[];
  tierForValue?: (value: number) => PerformanceTier;
  // Undefined and null both mean "no data this week" — a field the payload
  // hasn't got is a gap in the line, never a zero.
  valueForRow: (row: TRow) => number | null | undefined;
  onOpenScreenBreakdown: (weekStart: string) => void;
}) => {
  /* React Hooks */
  const instanceId = useId().replace(/:/g, "");
  const [hovered, setHovered] = useState<{
    index: number;
    x: number;
    y: number;
  } | null>(null);

  /* Helpers */
  const isTimeToScreen =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    isLowerBetter === undefined
      ? dashboardStafferStats.isTimeToScreen(kind)
      : isLowerBetter;
  const defaultGoal = isTimeToScreen
    ? SCREEN_HOURS_GREEN_MAX
    : dashboardStafferStats.submitGoal(TREND_PERIOD);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const chartGoal = goalOverride === undefined ? defaultGoal : goalOverride;

  const values: (number | null)[] = rows.map((row) => {
    const value = valueForRow(row);
    return value === undefined ? null : value;
  });

  const resolveTier = (value: number | null): PerformanceTier | null => {
    if (value === null) return null;
    if (tierForValue) return tierForValue(value);
    return dashboardStafferStats.tierFor(kind, value, TREND_PERIOD);
  };

  // Time to screen grades on a fixed hours ceiling; everything else is scaled
  // to its goal, which sits near the top of the plot.
  const goalLinePercent = isTimeToScreen
    ? STAFFER_STAT_GOAL_LINE_PERCENT
    : KPI_GOAL_LINE_PERCENT;

  const resolveY = (value: number | null): number | null => {
    if (value === null) return null;
    if (isTimeToScreen) {
      return dashboardStafferStats.screenChartPercent(value, chartTopPercent);
    }
    return dashboardKpis.goalAnchoredPercent(value, chartGoal);
  };

  const displayValue = (value: number | null): string =>
    formatValue
      ? formatValue(value)
      : dashboardStafferStats.formatValue(kind, value);

  const points: TrendPoint[] = rows.map((row, index) => {
    const value = values[index];
    return {
      label: dates.formatShortDate(row.weekStart),
      weekStart: row.weekStart,
      tier: resolveTier(value),
      value,
      x: rows.length === 1 ? 0 : (index / (rows.length - 1)) * 100,
      y: resolveY(value),
    };
  });

  const plotted = points.filter(
    (point): point is TrendPoint & { tier: PerformanceTier; y: number } =>
      point.y !== null && point.tier !== null,
  );
  // A line can climb well past the card's top border, and the smoothing takes
  // it further still; the mask has to cover wherever it actually reached or the
  // fill under it vanishes.
  const chartMinY =
    Math.min(0, chartTopPercent, ...plotted.map((point) => point.y)) -
    MASK_OVERSHOOT_PERCENT;
  // SVG ids are document-global, and two charts of the same kind render
  // different stops from their own data — so this has to be per instance, not
  // per metric, or one chart paints with the other's gradient.
  const gradientId = `staffer-trend-${instanceId}`;
  const linePath = smoothPath(plotted);
  const areaPath = plotted.length
    ? `${linePath} L ${plotted[plotted.length - 1].x.toString()} 100 L ${plotted[0].x.toString()} 100 Z`
    : "";
  const goalLabel = displayValue(chartGoal);

  /* Helpers */
  const weekIndexAt = (event: ReactMouseEvent<HTMLDivElement>): number => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - bounds.left) / bounds.width;
    return Math.min(
      points.length - 1,
      Math.max(0, Math.floor(ratio * points.length)),
    );
  };

  /* Handlers */
  const handleChartClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isTimeToScreen) return;
    onOpenScreenBreakdown(points[weekIndexAt(event)].weekStart);
  };

  const handleHoverMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    setHovered({
      index: weekIndexAt(event),
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleHoverLeave = () => {
    setHovered(null);
  };

  /* Render */
  const render = () => {
    if (plotted.length === 0) {
      return (
        <Column className="flex-1 items-center justify-center">
          <Text className="text-xs font-normal italic text-lever-text-label">
            {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
            {emptyLabel ||
              `No ${isTimeToScreen ? "screens" : "submits"} in the last five weeks`}
          </Text>
        </Column>
      );
    }

    return (
      <Column
        className={cn(
          "relative -mx-4 min-h-0 w-auto flex-1",
          isTimeToScreen && "cursor-pointer",
        )}
        onClick={handleChartClick}
      >
        <svg
          className="h-full w-full overflow-visible"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
              {gradientStops(plotted).map((stop) => (
                <stop
                  key={`${stop.color}-${stop.offset.toString()}`}
                  offset={`${stop.offset.toString()}%`}
                  stopColor={stop.color}
                />
              ))}
            </linearGradient>
            <linearGradient
              id={`${gradientId}-fade`}
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0.3" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            {/* Spans the drawn range, not just the svg box — a full-bleed
                time-to-screen line climbs above y=0 and would be masked out. */}
            <mask
              id={`${gradientId}-mask`}
              maskUnits="userSpaceOnUse"
              x="0"
              y={chartMinY}
              width="100"
              height={100 - chartMinY}
            >
              <rect
                x="0"
                y={chartMinY}
                width="100"
                height={100 - chartMinY}
                fill={`url(#${gradientId}-fade)`}
              />
            </mask>
          </defs>
          <line
            x1="0"
            x2="100"
            y1={goalLinePercent}
            y2={goalLinePercent}
            className="stroke-lever-icon-label/45"
            strokeDasharray="4 3"
            strokeLinecap="butt"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={areaPath}
            fill={`url(#${gradientId})`}
            mask={`url(#${gradientId}-mask)`}
          />
          <path
            d={linePath}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        <Column
          className="pointer-events-none absolute right-1 -translate-y-full"
          style={{ top: `${goalLinePercent.toString()}%` }}
        >
          <Text className="font-lever-accent text-[9px] font-medium uppercase tabular-nums tracking-wider text-lever-text-label">
            {goalLabel}
          </Text>
        </Column>

        <div
          className="absolute inset-0"
          onMouseLeave={handleHoverLeave}
          onMouseMove={handleHoverMove}
        />

        <div
          className="pointer-events-none absolute inset-x-2 bottom-1.5 grid"
          style={{
            gridTemplateColumns: `repeat(${rows.length.toString()}, minmax(0, 1fr))`,
          }}
        >
          {points.map((point) => (
            <Text
              key={point.label}
              className="text-center font-lever-accent text-[9px] font-medium uppercase tabular-nums tracking-wider text-lever-text-body"
            >
              {displayValue(point.value)}
            </Text>
          ))}
        </div>

        {hovered ? (
          <TrendWeekTooltip
            label={points[hovered.index].label}
            tier={points[hovered.index].tier}
            value={displayValue(points[hovered.index].value)}
            x={hovered.x}
            y={hovered.y}
          />
        ) : null}
      </Column>
    );
  };

  return render();
};
